import { useUserEventLogger } from '@repo/tracking-service';
import { Divider, List, Title } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import { graphql, useFragment } from 'react-relay';

import { CorpsSection$key } from '@/__generated__/CorpsSection.graphql';
import { setCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import RefundSummaryCard from '@/components/tax-refund/summary/RefundSummaryCard';
import { StatusManualSection } from '@/components/tax-refund/summary/StatusManualSection';
import { APPLIED_OR_FINISHED_REFUND_STATUSES } from '@/lib/constants/home';
import { COMMON_PATHS, CORPORATION_PATHS } from '@/lib/constants/paths';
import { zCurrency } from '@/lib/utils/zCurrency';

type Props = {
  fragmentRef: CorpsSection$key;
  onClickLookup: () => void;
};

const CorpsSection = ({ fragmentRef, onClickLookup }: Props) => {
  const { sendClickEvent } = useUserEventLogger();
  const router = useRouter();

  const data = useFragment(
    graphql`
      fragment CorpsSection on Query {
        bznavRefund {
          user {
            corps {
              refundId
              refundStatus
              refundStage
              refundSubStage
              refundAmount
              refundReportDt
              refundResultDt
              refundResultExpectDt
              paymentClosingDt
              paymentAmount
              applicationDt
              cancelStage
              userFee
              lostReason
              tin
              office
              virtualAccountInfo {
                owner
                bank
                account
                paymentAmount
              }
              partnerInfo {
                partnerName
                feeRate
                defaultFeeRate
              }
              businessInfo {
                hometaxBizName
                hometaxBizNo
              }
            }
          }
        }
      }
    `,
    fragmentRef
  );
  const corpsItems = data?.bznavRefund?.user?.corps;

  const setRefundData = (item: any) => {
    setCorporationRefundData({
      bizName: item?.businessInfo?.hometaxBizName,
      refundId: item?.refundId,
      refundStatus: item?.refundStatus,
      refundAmount: item?.refundAmount,
      partnerName: item?.partnerInfo?.partnerName,
      feeRate: item?.partnerInfo?.feeRate,
      defaultFeeRate: item?.partnerInfo?.defaultFeeRate,
      tin: item?.tin,
      serviceAgreementFlow: false
    });
  };

  const getCardTitle = (isRetry: boolean, amount: number) => {
    if (isRetry) return '환급금 새로 조회';
    return zCurrency(amount ?? 0, '원', true, '0');
  };

  const getButtonState = (item: any) => {
    const refundStatus = item?.refundStatus;
    const isRetry = refundStatus === 'Retry';
    const isApplyImpossible = refundStatus === 'ApplyImpossible';
    const isAppliedRefund = APPLIED_OR_FINISHED_REFUND_STATUSES.includes(refundStatus);

    if (isRetry)
      return {
        title: '다시조회',
        onClick: () => {
          setRefundData(item);
          onClickLookup();
        }
      };
    if (!isApplyImpossible && !isAppliedRefund)
      return {
        title: '환급받기',
        onClick: () => {
          setRefundData(item);
          router.push({
            pathname: CORPORATION_PATHS.법인환급액조회결과,
            query: { type: 'corps' }
          });
        }
      };
    if (isApplyImpossible)
      return {
        title: '',
        onClick: () => {
          setRefundData(item);
          router.push({
            pathname: CORPORATION_PATHS.법인환급액조회결과,
            query: { type: 'corps' }
          });
        }
      };
    if (isAppliedRefund)
      return {
        title: '상세',
        onClick: () => {
          setRefundData(item);
          router.push({
            pathname: COMMON_PATHS.환급신청결과,
            query: { type: 'corps' }
          });
        }
      };
    return {
      title: ''
    };
  };

  return (
    <>
      {corpsItems && (
        <div>
          <Title.Section size="medium" title="법인사업자" />
          <div className={'flex flex-col gap-4'}>
            {corpsItems?.map((item: any, index: number) => (
              <div
                key={item?.refundId ?? index}
                className={`rounded-5 bg-white cursor-pointer transition-transform duration-150 ease-in-out hover:scale-[0.97]`}
              >
                <RefundSummaryCard
                  cardType={'corps'}
                  cardLabel={item?.businessInfo?.hometaxBizName ?? ''}
                  cardTitle={getCardTitle(item.refundStatus === 'Retry', Number(item.refundAmount ?? 0))}
                  cardSubTitle={item?.businessInfo?.hometaxBizNo ?? ''}
                  buttonState={getButtonState(item)}
                  refundItem={item}
                  cardAnimate={false}
                />
                {APPLIED_OR_FINISHED_REFUND_STATUSES.includes(item.refundStatus) && (
                  <>
                    <div className={'px-6'}>
                      <Divider.Line />
                    </div>
                    <StatusManualSection item={item} sectionType={'corps'} />
                  </>
                )}
              </div>
            ))}
            {corpsItems.length < 5 && (
              <div
                className={'rounded-4 cursor-pointer overflow-hidden bg-white px-6 py-2 hover:scale-[0.97] ease-in-out transition-transform duration-150'}
                onClick={() => {
                  sendClickEvent('home_body_corps-add');
                  onClickLookup();
                }}
              >
                <List.Basic
                  left={<img src={'/images/illus-icons/icg_plus_md.svg'} className={'h-10 w-10'} alt="add-icon" />}
                  title={{ text: '법인사업체 추가' }}
                  right={{ type: 'text', component: '최대 5개' }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CorpsSection;
